import { useCallback, useState } from "react";
import * as Sentry from "@sentry/react";

import { JoinCodePanel } from "@/components/dashboard/CampaignDetailViews";
import {
  MonolithBoardModal,
  MONOLITH_BOARD_MODAL_TITLE_CLASS,
} from "@/components/dashboard/MonolithBoardModal";
import { Button } from "@/components/ui/Button";
import { useToast } from "@/components/ui/ToastProvider";
import { useUpdateJoinCodeMutation } from "@/lib/queries/campaignQueries";
import type { UserCampaignRecord } from "@west-marches/shared";

interface InviteModalProps {
  open: boolean;
  onClose: () => void;
  onOpenMembers?: () => void;
  campaign: UserCampaignRecord | null;
  onCampaignUpdate: (updates: Partial<Pick<UserCampaignRecord, "joinCode" | "selfJoinEnabled">>) => void;
}

function reportInviteError(action: string, error: unknown, extras?: Record<string, unknown>) {
  Sentry.captureException(error instanceof Error ? error : new Error(action), {
    tags: { area: "invite-modal", action },
    extra: extras,
  });
}

function CloseIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-3.5 w-3.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M6 6 18 18" />
      <path d="M18 6 6 18" />
    </svg>
  );
}

export default function InviteModal({
  open,
  onClose,
  onOpenMembers,
  campaign,
  onCampaignUpdate,
}: InviteModalProps) {
  const toast = useToast();
  const updateJoinCodeMutation = useUpdateJoinCodeMutation();
  const [rotatingJoinCode, setRotatingJoinCode] = useState(false);
  const [togglingSelfJoin, setTogglingSelfJoin] = useState(false);

  const handleRotateJoinCode = useCallback(async () => {
    if (!campaign) return;
    setRotatingJoinCode(true);
    try {
      const result = await updateJoinCodeMutation.mutateAsync({
        campaignId: campaign.id,
        input: { regenerate: true },
      });
      onCampaignUpdate({ joinCode: result.joinCode });
      toast.success("Invite link updated.");
    } catch (error) {
      reportInviteError("rotate-invite-link", error, { campaignId: campaign.id });
      toast.error(error instanceof Error ? error.message : "Failed to update invite link.");
    } finally {
      setRotatingJoinCode(false);
    }
  }, [campaign, onCampaignUpdate, toast, updateJoinCodeMutation]);

  const handleToggleSelfJoin = useCallback(
    async (enabled: boolean) => {
      if (!campaign) return;
      setTogglingSelfJoin(true);
      try {
        await updateJoinCodeMutation.mutateAsync({
          campaignId: campaign.id,
          input: { selfJoinEnabled: enabled },
        });
        onCampaignUpdate({ selfJoinEnabled: enabled });
        toast.success(enabled ? "Self-join enabled." : "Self-join disabled.");
      } catch (error) {
        reportInviteError("toggle-self-join", error, { campaignId: campaign.id });
        toast.error(error instanceof Error ? error.message : "Failed to update self-join.");
      } finally {
        setTogglingSelfJoin(false);
      }
    },
    [campaign, onCampaignUpdate, toast, updateJoinCodeMutation],
  );

  const submitting = rotatingJoinCode || togglingSelfJoin;
  const handleOpenMembers = useCallback(() => {
    onClose();
    onOpenMembers?.();
  }, [onClose, onOpenMembers]);

  return (
    <MonolithBoardModal
      open={open && campaign !== null}
      titleId="invite-players-modal-title"
      onClose={onClose}
      submitting={submitting}
    >
      <div className="relative">
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          aria-label="Close invite players dialog"
          title="Close invite players dialog"
          className="absolute top-0 right-0 z-20 inline-flex h-8 w-8 translate-x-1/3 -translate-y-1/3 items-center justify-center rounded-full border-[3px] border-neutral-950 bg-[#fffaf1] text-neutral-950 shadow-sm transition-colors hover:bg-[#fff2d9] disabled:cursor-not-allowed disabled:opacity-60"
        >
          <CloseIcon />
        </button>
        <h2 id="invite-players-modal-title" className={MONOLITH_BOARD_MODAL_TITLE_CLASS}>
          Invite players
        </h2>
      </div>
      <p className="mt-3 text-sm leading-relaxed text-neutral-700">
        Share the invite link with players so they can join this campaign.
      </p>
      <div className="mt-6">
        {campaign ? (
          <JoinCodePanel
            campaign={campaign}
            onRotateJoinCode={handleRotateJoinCode}
            rotatingJoinCode={rotatingJoinCode}
            onToggleSelfJoin={handleToggleSelfJoin}
            togglingSelfJoin={togglingSelfJoin}
            actionSlot={
              onOpenMembers ? (
                <Button
                  type="button"
                  variant="secondary"
                  chrome="compact"
                  className="px-3 py-2 text-[10px]"
                  disabled={submitting}
                  onClick={handleOpenMembers}
                >
                  Members
                </Button>
              ) : undefined
            }
          />
        ) : null}
      </div>
    </MonolithBoardModal>
  );
}
